import React, { useEffect, useState, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import CustomerSearch from "@/components/CustomerSearch";
import { Customer } from "@/types";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { XIcon } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LEAD_SOURCES = [
  "Angie's List",
  "Facebook",
  "Friend (Word of Mouth)",
  "Google Search",
  "Instagram",
  "Nextdoor",
  "Postcard (Direct Mail)",
  "Realtor Referral",
  "Repeat Customer",
  "Saw our Truck",
  "Thumbtack",
  "Yelp",
  "Somewhere Else",
] as const;

const CUSTOM_SOURCES = ["Realtor Referral", "Somewhere Else"];

const CustomerInfo: React.FC = () => {
  const [linkedCustomer, setLinkedCustomer] = useState<Customer | null>(null);
  const { control, watch, setValue } = useFormContext();
  const [mobilePhoneNumber, setMobilePhoneNumber] = useState("");
  const [alternatePhoneNumber, setAlternatePhoneNumber] = useState("");
  const [showOtherLeadSource, setShowOtherLeadSource] = useState(false);
  const otherSourceInputRef = useRef<HTMLInputElement>(null);

  const customerMobilePhone = watch("customer_mobile_phone");
  const customerAlternatePhone = watch("customer_alternate_phone");

  useEffect(() => {
    if (customerMobilePhone) {
      setMobilePhoneNumber(formatPhoneNumber(customerMobilePhone));
    }
  }, [customerMobilePhone]);

  useEffect(() => {
    if (customerAlternatePhone) {
      setAlternatePhoneNumber(formatPhoneNumber(customerAlternatePhone));
    }
  }, [customerAlternatePhone]);

  const handleMobilePhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatPhoneNumber(event.target.value);
    setMobilePhoneNumber(formatted);
  };

  const handleAlternatePhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatPhoneNumber(event.target.value);
    setAlternatePhoneNumber(formatted);
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "mobile_phone") {
        const formattedNumber = formatPhoneNumber(value.mobile_phone);
        setValue("mobile_phone", formattedNumber, {
          shouldValidate: true,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    if (showOtherLeadSource) {
      otherSourceInputRef.current?.focus();
    }
  }, [showOtherLeadSource]);

  return (
    <div className="relative" id="customer-info">
      <div className="w-full flex flex-row items-baseline gap-4">
        {linkedCustomer ? (
          <div className="flex items-center gap-1">
            <span className="text-sm">Repeat Customer:</span>
            <span className="text-sm font-bold">
              <Link
                to={`/customer/${linkedCustomer.id}`}
                target="_blank"
                className="text-black font-semibold underline underline-offset-4 decoration-yellow-500 cursor-pointer"
              >
                {linkedCustomer.name}
              </Link>
            </span>
            <span className="text-sm text-gray-700">
              (you can override details for this move below)
            </span>
            <Button
              className="px-1 text-black font-semibold underline underline-offset-4"
              variant="link"
              onClick={() => {
                setLinkedCustomer(null);
                setValue("source", "");
                setShowOtherLeadSource(false);
              }}
            >
              <XIcon className="w-4 h-4 mr-0.5" /> Unlink
            </Button>
          </div>
        ) : (
          <div className="hidden w-full grid grid-cols-2 gap-4">
            <FormItem>
              <FormLabel className="block">Look up repeat customer</FormLabel>
              <FormControl>
                <CustomerSearch
                  placeholder="Search by name, phone, or email"
                  onSelectCustomer={(customer) => {
                    setValue("customer_name", customer.name);
                    setValue("customer_mobile_phone", customer.mobile_phone);
                    setMobilePhoneNumber(
                      formatPhoneNumber(customer?.mobile_phone || "")
                    );
                    setValue(
                      "customer_alternate_phone",
                      customer.alternate_phone
                    );
                    setAlternatePhoneNumber(
                      formatPhoneNumber(customer?.alternate_phone || "")
                    );
                    setValue("customer_email", customer.email);
                    setValue("customer_id", customer.id);
                    setValue("customer_source", "Repeat Customer");
                    setLinkedCustomer(customer);
                    setShowOtherLeadSource(false);
                  }}
                />
              </FormControl>
            </FormItem>
          </div>
        )}
      </div>
      {/* hidden formfield for customer_id */}
      <FormField
        control={control}
        name="customer_id"
        render={({ field }) => <Input type="hidden" {...field} />}
      />
      <div className="grid grid-cols-1 gap-4 mt-2">
        <FormField
          control={control}
          name="customer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex flex-row items-center gap-2">
                Name
                <FormMessage />
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="customer_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex flex-row items-center gap-2">
                Email
                <FormMessage />
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <FormField
            control={control}
            name="customer_mobile_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex flex-row items-center gap-2">
                  Phone
                  <FormMessage />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="Primary"
                    value={mobilePhoneNumber}
                    onChange={(e) => {
                      handleMobilePhoneChange(e);
                      field.onChange(e.target.value);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {/* can text primary */}
          {/* <Controller
            control={control}
            name="can_text_primary"
            render={({ field }) => (
              <FormItem className="mt-2 flex flex-row items-center space-x-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    id="can-text-primary"
                    {...field}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <Label htmlFor="can-text-primary">Permission to text?</Label>
                <FormMessage />
              </FormItem>
            )}
          /> */}
        </div>
        <Controller
          name="customer_alternate_phone"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Alternate Phone</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="tel"
                  value={alternatePhoneNumber}
                  placeholder="Alternate"
                  onChange={(e) => {
                    handleAlternatePhoneChange(e);
                    field.onChange(e.target.value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 mt-4">
        {!linkedCustomer && (
          <FormField
            control={control}
            name="customer_source"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="block">
                  How did they find you?
                  {showOtherLeadSource && (
                    <a
                      href="#"
                      className="ml-2 px-1 text-black font-semibold inline-block"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowOtherLeadSource(false);
                        field.onChange("");
                        setValue("customer_source", "");
                        setValue("customer_source_other", "");
                      }}
                    >
                      (clear)
                    </a>
                  )}
                </FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (CUSTOM_SOURCES.includes(value)) {
                        setValue("customer_source", value);
                        setShowOtherLeadSource(true);
                      } else {
                        setValue("customer_source_other", "");
                        setShowOtherLeadSource(false);
                      }
                    }}
                    value={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {showOtherLeadSource && (
          <FormField
            control={control}
            name="customer_source_other"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {watch("customer_source") === "Somewhere Else" &&
                    "Where did they hear about you?"}
                  {watch("customer_source") === "Realtor Referral" &&
                    "Realtor Name"}
                </FormLabel>
                <Input
                  {...field}
                  ref={otherSourceInputRef}
                  placeholder="Enter"
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue("customer_source_other", e.target.value);
                  }}
                />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerInfo;
